/**
 * Modern NavContainer Component
 * 
 * A modernized navigation container with improved Material Design 3 styling
 * and better user experience for the Room Designer application.
 */

import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Tooltip,
  Badge,
  useTheme as useMuiTheme,
  alpha,
  Typography,
  IconButton,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChevronRight,
  Warning as WarningIcon,
  Edit as EditIcon,
  GridOn as CeilingIcon,
  GraphicEq as AcousticsIcon,
  Mic as MicrophonesIcon,
  VolumeUp as SpeakersIcon,
  Videocam as CamerasIcon,
  List as EquipmentListIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { SvgIcon } from '@mui/material';
import MuiNavSection from './NavSection';
import FloorplanControls from './FloorplanControls';
import WalkthroughTitle from '../atoms/WalkthroughTitle';
import ToolContext from 'components/services/ToolContext';
import { useTheme } from 'theme/ThemeContext';
import ReducersIndex from 'reduxModules/ducks';
import { getErrorState } from 'reduxModules/ducks/ui';
import { keys } from 'lodash';
import { NavContainerProps } from 'types/components/organisms';
import { AnyAction } from '@reduxjs/toolkit';
import tutorialSteps from '../../toolConstants/conferenceroomDesigner/tutorial';
import type { RootState } from 'types/redux/state';

/**
 * Interface for navigation menu items
 */
interface NavMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  children?: NavMenuItem[];
  hasWarning?: boolean;
}

/**
 * Custom Floor Plan Icon component
 */
const FloorPlanIcon: React.FC<any> = (props) => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M2 3.2C2 2.53726 2.53726 2 3.2 2H12.8C13.4627 2 14 2.53726 14 3.2V12.8C14 13.4627 13.4627 14 12.8 14H3.2C2.53726 14 2 13.4627 2 12.8V8.68571C2 8.40168 2.23025 8.17143 2.51429 8.17143C2.79832 8.17143 3.02857 8.40168 3.02857 8.68571V9.31429H7.30893V6.62857C7.30893 6.34454 7.53918 6.11429 7.82321 6.11429C8.10725 6.11429 8.3375 6.34454 8.3375 6.62857V9.31429H9.37143C9.65546 9.31429 9.88571 9.54454 9.88571 9.82857C9.88571 10.1126 9.65546 10.3429 9.37143 10.3429H3.02857V12.8C3.02857 12.8947 3.10532 12.9714 3.2 12.9714H12.8C12.8947 12.9714 12.9714 12.8947 12.9714 12.8V10.3429H11.7714C11.4874 10.3429 11.2571 10.1126 11.2571 9.82857C11.2571 9.54454 11.4874 9.31429 11.7714 9.31429H12.9714V3.2C12.9714 3.10532 12.8947 3.02857 12.8 3.02857H8.3375V4.57143C8.3375 4.85546 8.10725 5.08571 7.82321 5.08571C7.53918 5.08571 7.30893 4.85546 7.30893 4.57143V3.02857H3.2C3.10532 3.02857 3.02857 3.10532 3.02857 3.2V6.28571C3.02857 6.56975 2.79832 6.8 2.51429 6.8C2.23025 6.8 2 6.56975 2 6.28571V3.2Z" 
      fill="currentColor"
      stroke="currentColor" 
      strokeWidth="0.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </SvgIcon>
);

/**
 * Custom Furniture Icon component
 */
const FurnitureIcon: React.FC<any> = (props) => (
  <SvgIcon {...props} viewBox="0 0 16 14">
    <path 
      d="M6.5 0.5C7.0523 0.5 7.5 0.9477 7.5 1.5V2.2002H8.5V1.5C8.5 0.9477 8.9477 0.5 9.5 0.5H10.5C11.0523 0.5 11.5 0.9477 11.5 1.5V2.2471C12.6452 2.4537 13.5463 3.3548 13.7529 4.5H14.5C15.0523 4.5 15.5 4.9477 15.5 5.5V6.5C15.5 7.0523 15.0523 7.5 14.5 7.5H13.7529C13.5463 8.6452 12.6451 9.5453 11.5 9.752V10.5C11.5 11.0523 11.0523 11.5 10.5 11.5H9.5C8.9477 11.5 8.5 11.0523 8.5 10.5V9.7998H7.5V10.5C7.5 11.0523 7.0523 11.5 6.5 11.5H5.5C4.9477 11.5 4.5 11.0523 4.5 10.5V9.752C3.3549 9.5453 2.4537 8.6452 2.2471 7.5H1.5C0.9477 7.5 0.5 7.0523 0.5 6.5V5.5C0.5 4.9477 0.9477 4.5 1.5 4.5H2.2471C2.4537 3.3548 3.3548 2.4537 4.5 2.2471V1.5C4.5 0.9477 4.9477 0.5 5.5 0.5H6.5ZM5 3.7998C4.3373 3.7998 3.7998 4.3373 3.7998 5V7C3.7998 7.6627 4.3373 8.2002 5 8.2002H11C11.6627 8.2002 12.2002 7.6627 12.2002 7V5C12.2002 4.3373 11.6627 3.7998 11 3.7998H5Z" 
      fill="currentColor"
    />
  </SvgIcon>
);

/**
 * Modern NavContainer component
 * 
 * @returns The modernized NavContainer component
 */
const MuiNavContainer: React.FC<NavContainerProps> = ({ onOpenDesignNameDialog }) => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme();
  const tool = useContext(ToolContext) as any;
  
  // Redux state
  const showing = useSelector((state: RootState) => state.ui.showing);
  const items = keys(tool.menus).filter(key => showing in tool.menus[key]);
  
  const active = useSelector((state: RootState) => state.ui.currentView);
  const dispatch = useDispatch();
  const error = useSelector((state: any) => (getErrorState as any)(state));
  const exit = useSelector((state: RootState) => state.ui.exitToRoom);
  const projectName = useSelector((state: RootState) => state.container.project.name);
  const projectCompany = useSelector((state: RootState) => state.container.project.company);
  const guideActiveSection = useSelector((state: RootState) => state.ui.spotlightTarget);
  const tutorialStep = useSelector((state: RootState) => state.ui.tutorialStep);
  const tutorialConfig = useSelector((state: RootState) => state.ui.tutorialConfig);
  
  // Disable animations when guide is active
  const collapseTimeout = guideActiveSection ? 0 : "auto";

  // Check if we're on the Name design screen in the walkthrough guide
  const steps = tutorialConfig || tutorialSteps;
  const currentStep = steps && tutorialStep !== undefined ? steps[tutorialStep] : null;
  const isNameDesignScreen = currentStep?.isNameDesignScreen === true;

  // Local state for expanded sections
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});


  const handleMenuItemClick = (itemId: string) => {
    // If guide is active, only allow clicking on the current guide section
    if (guideActiveSection) {
      // Normalize the itemId and guideActiveSection for comparison
      const normalizedItemId = itemId === 'floorplan' ? 'floor_plan' :
                               itemId === 'equipmentlist' ? 'equipment_list' : itemId;

      // Block navigation to different sections during guide
      if (normalizedItemId !== guideActiveSection) {
        return;
      }
    }

    // Accordion behavior: only one section can be expanded at a time
    if (itemId === 'floorplan' || itemId === 'ceiling' || itemId === 'furnishing' || itemId === 'acoustics' || itemId === 'microphones' || itemId === 'speakers' || itemId === 'camera' || itemId === 'equipmentlist') {


      // Batch state updates to prevent double renders
      setExpandedItems(prev => {
        // If clicking the same item, toggle it
        if (prev[itemId]) {
          const newState = { ...prev, [itemId]: false };
          return newState;
        }
        // Otherwise, close all sections and open the clicked one
        const newState = {
          floorplan: false,
          ceiling: false,
          furnishing: false,
          acoustics: false,
          microphones: false,
          speakers: false,
          camera: false,
          equipmentlist: false,
          [itemId]: true
        };
        return newState;
      });

      // Dispatch Redux action after local state update
      const action = ReducersIndex.uiReducers.setCurrentView(itemId);
      if (action) {
        dispatch(action as unknown as AnyAction);
      }
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Function to get icon for navigation items
  const getNavIcon = (itemKey: string, originalItem: string) => {
    const iconStyle = { fontSize: '20px' };
    const iconMap: Record<string, React.ReactNode> = {
      'floorplan': <FloorPlanIcon sx={iconStyle} />,
      'floor_plan': <FloorPlanIcon sx={iconStyle} />,
      'ceiling': <CeilingIcon sx={iconStyle} />,
      'furniture': <FurnitureIcon sx={iconStyle} />,
      'furnishing': <FurnitureIcon sx={iconStyle} />,
      'acoustics': <AcousticsIcon sx={iconStyle} />,
      'microphones': <MicrophonesIcon sx={iconStyle} />,
      'speakers': <SpeakersIcon sx={iconStyle} />,
      'cameras': <CamerasIcon sx={iconStyle} />,
      'camera': <CamerasIcon sx={iconStyle} />,
      'equipmentlist': <EquipmentListIcon sx={iconStyle} />,
    };
    
    return iconMap[itemKey] || iconMap[originalItem.replace('_', '').toLowerCase()] || iconMap[originalItem.toLowerCase()];
  };

  useEffect(() => {
    if (items.length > 0 && !items.map(item => item.replace('_', '')).includes(active)) {
      const action = ReducersIndex.uiReducers.setCurrentView(items[0].replace('_', ''));
      if (action) {
        dispatch(action as unknown as AnyAction);
      }
    } else if (items.length > 0 && active) {
      // If active view is already valid, re-dispatch setCurrentView to ensure
      // device calculations and heatmap state are properly initialized
      // This is especially important when loading an existing design
      const action = ReducersIndex.uiReducers.setCurrentView(active);
      if (action) {
        dispatch(action as unknown as AnyAction);
      }
    }
  }, [items, active, dispatch]);

  // Automatically expand the section when the active view changes (e.g., from tutorial)
  useEffect(() => {
    if (active) {
      // Normalize the key by removing underscores
      const itemKey = active.replace(/_/g, '').toLowerCase();

      // Map specific keys if needed
      let mappedKey = itemKey;
      if (itemKey === 'floorplan') mappedKey = 'floorplan';
      else if (itemKey === 'equipmentlist') mappedKey = 'equipmentlist';

      if (mappedKey === 'floorplan' || mappedKey === 'ceiling' || mappedKey === 'furnishing' ||
          mappedKey === 'acoustics' || mappedKey === 'microphones' || mappedKey === 'speakers' ||
          mappedKey === 'camera' || mappedKey === 'equipmentlist') {
        setExpandedItems(prev => {
          // Only update if not already expanded and other sections need to be closed
          if (!prev[mappedKey] || Object.keys(prev).some(key => key !== mappedKey && prev[key])) {
            const newState = {
              floorplan: false,
              ceiling: false,
              furnishing: false,
              acoustics: false,
              microphones: false,
              speakers: false,
              camera: false,
              equipmentlist: false,
              [mappedKey]: true
            };
            // Only update if the state actually changed
            if (JSON.stringify(prev) !== JSON.stringify(newState)) {
              return newState;
            }
          }
          return prev;
        });
      }
    }
  }, [active]);

  return (
    <Box 
      sx={{
        width: 320,
        height: '100%',
        backgroundColor: mode === 'dark' ? '#222222' : '#f5f5f5',  // Theme-aware background
        borderRight: `1px solid ${muiTheme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
        zIndex: 1300,
        padding: '25px',
      }}
    >
      {/* Project Title Section */}
      <Box 
        sx={{ 
          p: 3,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '8px',  // Match mx: 1 of nav sections
            right: '8px', // Match mx: 1 of nav sections
            height: '1px',
            backgroundColor: muiTheme.palette.divider,
          }
        }}
      >
        <Box id="projectName" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <WalkthroughTitle>
              {projectName || 'Conference Room Md'}
            </WalkthroughTitle>
          </Box>
          <IconButton
            size="small"
            disabled={isNameDesignScreen}
            sx={{
              color: mode === 'dark' ? '#e0e0e0' : '#6B7280',
              p: 0.5,
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
              },
              '&.Mui-disabled': {
                opacity: 0.3,
                color: mode === 'dark' ? '#e0e0e0' : '#6B7280',
              }
            }}
            onClick={() => {
              if (onOpenDesignNameDialog) {
                onOpenDesignNameDialog();
              }
            }}
          >
            <EditIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        </Box>
        <Box sx={{ margin: '0 0 20px 0'}}>
          {projectCompany && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                color: mode === 'dark' ? '#e0e0e0' : '#6B7280',
                fontWeight: 400,
              }}
            >
              {projectCompany}
            </Typography>
          )}
        </Box>
      </Box>
      
      {/* Main navigation menu */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
        }}
      >
        <List sx={{ py: 1 }}>
          {items.map((item) => {
            const itemKey = item.replace('_', '');
            const isActive = active === itemKey;
            const hasError = error && error[item];
            const isExpanded = expandedItems[itemKey];
            const isFloorPlan = item === 'floor_plan';
            const isCeiling = item === 'ceiling';
            const isFurniture = item === 'furnishing';
            const isAcoustics = item === 'acoustics';
            const isMicrophones = item === 'microphones';
            const isSpeakers = item === 'speakers';
            const isCamera = item === 'camera';
            const isEquipmentList = item === 'equipment_list';
            const hasExpandableContent = isFloorPlan || isCeiling || isFurniture || isAcoustics || isMicrophones || isSpeakers || isCamera || isEquipmentList;
            
            return (
              <Box
                key={item}
                id={itemKey === 'floorplan' ? 'floor_plan' : itemKey === 'equipmentlist' ? 'equipment_list' : itemKey}
                data-nav-section={itemKey}
                data-expanded={isExpanded ? 'true' : 'false'}
              >
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => handleMenuItemClick(itemKey)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      mx: 0,
                      borderRadius: '8px',
                      backgroundColor: isActive
                        ? (mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)')  // Theme-aware active background
                        : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isActive
                          ? (mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)')
                          : (mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
                      },
                      '&.Mui-selected': {
                        backgroundColor: isActive
                          ? (mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)')
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive
                            ? (mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)')
                            : (mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
                        },
                      },
                    }}
                  >
                    {hasExpandableContent && (
                      <ListItemIcon 
                        sx={{ 
                          minWidth: '24px',
                          color: isActive ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                        }}
                      >
                        <ChevronRight 
                          sx={{ 
                            fontSize: '18px',
                            color: isActive ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease-in-out',
                          }} 
                        />
                      </ListItemIcon>
                    )}
                    <ListItemIcon 
                      sx={{ 
                        minWidth: '36px',
                        color: isActive ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                      }}
                    >
                      {getNavIcon(itemKey, item)}
                    </ListItemIcon>
                    <ListItemText 
                      primary={(() => {
                        const displayNames: Record<string, string> = {
                          'furnishing': 'Furniture',
                          'floor_plan': 'Floor plan',
                          'ceiling': 'Ceiling',
                          'acoustics': 'Acoustics',
                          'microphones': 'Microphones', 
                          'speakers': 'Speakers',
                          'camera': 'Cameras',
                          'cameras': 'Cameras',
                          'equipment_list': 'Equipment list'
                        };
                        return displayNames[item] || item.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
                      })()}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: '14px',
                          fontFamily: muiTheme.typography.fontFamily,
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                          letterSpacing: '0.01em',
                        }
                      }}
                    />
                    {hasError && (
                      <Tooltip title="Validation warning">
                        <Badge color="warning" variant="dot">
                          <WarningIcon
                            fontSize="small"
                            sx={{ color: muiTheme.palette.warning.main }}
                          />
                        </Badge>
                      </Tooltip>
                    )}
                    {!guideActiveSection && isActive && (
                      <Tooltip
                        title="Walkthrough Guide"
                        placement="right"
                        arrow
                        enterDelay={500}
                        leaveDelay={200}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: muiTheme.palette.background.paper,
                              color: muiTheme.palette.text.primary,
                              fontSize: '12px',
                              fontWeight: 500,
                              padding: '6px 12px',
                              border: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[200]}`,
                              boxShadow: muiTheme.shadows[1],
                              '& .MuiTooltip-arrow': {
                                color: muiTheme.palette.background.paper,
                                '&::before': {
                                  border: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[200]}`,
                                },
                              },
                            },
                          },
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0.5,
                            ml: 1,
                            color: muiTheme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();

                            // SIMPLIFIED APPROACH: Use only hasCompletedTutorial flag and spotlightTarget
                            // REVERT: Replace with original tutorial modal approach if needed


                            // Set hasCompletedTutorial to false to allow WalkthroughGuide to show
                            localStorage.setItem('hasCompletedTutorial', 'false');

                            // HACK: Clear the spotlight first to force WalkthroughGuide to reset
                            dispatch(ReducersIndex.uiReducers.setSpotlightTarget(null) as unknown as AnyAction);

                            // Get tutorial steps from tool context
                            const tutorialSteps = tool.tutorialSteps;

                            if (!tutorialSteps || tutorialSteps.length === 0) {
                              console.error('❌ No tutorial steps available!');
                              return;
                            }

                            // Map the current item to its menu name for the walkthrough
                            const menuMapping: Record<string, string> = {
                              'floor_plan': 'floor_plan',
                              'floorplan': 'floor_plan',
                              'ceiling': 'ceiling',
                              'furnishing': 'furnishing',
                              'acoustics': 'acoustics',
                              'microphones': 'microphones',
                              'speakers': 'speakers',
                              'camera': 'camera',
                              'equipment_list': 'equipmentlist',
                              'equipmentlist': 'equipmentlist'
                            };

                            const menuName = menuMapping[item] || menuMapping[item.replace('_', '').toLowerCase()];

                            // Find the step index for this menu
                            const targetStep = tutorialSteps.findIndex((step: any) => step.menu === menuName);

                            // Use setTimeout to allow WalkthroughGuide to reset after clearing spotlight
                            setTimeout(() => {
                              if (targetStep >= 0) {
                                const step = tutorialSteps[targetStep];

                                // Set the tutorial config
                                dispatch(ReducersIndex.uiReducers.setTutorialConfig(tutorialSteps) as unknown as AnyAction);

                                // Set the starting step
                                dispatch(ReducersIndex.uiReducers.setTutorialStep(targetStep) as unknown as AnyAction);

                                // CRITICAL: Reset isFirstTimeUser to true to reset WalkthroughGuide visibility
                                dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(true) as unknown as AnyAction);

                                // Set spotlight target to trigger WalkthroughGuide
                                const spotlightTarget = step?.target || item;
                                dispatch(ReducersIndex.uiReducers.setSpotlightTarget(spotlightTarget) as unknown as AnyAction);

                              } else {
                                console.warn('⚠️ No matching tutorial step found, using fallback');

                                // Set basic config and spotlight
                                dispatch(ReducersIndex.uiReducers.setTutorialConfig(tutorialSteps) as unknown as AnyAction);
                                dispatch(ReducersIndex.uiReducers.setTutorialStep(0) as unknown as AnyAction);
                                dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(true) as unknown as AnyAction);
                                dispatch(ReducersIndex.uiReducers.setSpotlightTarget(item) as unknown as AnyAction);
                              }
                            }, 100);
                          }}
                        >
                          <Box
                            component="img"
                            src={mode === 'light' ? '/images/info-light.svg' : '/images/info-dark.svg'}
                            alt="Help"
                            sx={{
                              width: '20px',
                              height: '20px',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemButton>
                </ListItem>
                
                {/* Expandable content for floor plan */}
                {isFloorPlan && (
                  <Collapse in={isExpanded} timeout={collapseTimeout} unmountOnExit>
                    <Box
                      sx={{
                        mx: 1,
                        borderRadius: '0 0 6px 6px',
                        p: 2,
                      }}
                    >
                      <FloorplanControls />
                      <Box sx={{ height: '15px' }} />
                    </Box>
                  </Collapse>
                )}
                
                {/* Expandable content for ceiling */}
                {isCeiling && (
                  <Collapse in={isExpanded} timeout={collapseTimeout} unmountOnExit>
                    <Box sx={{ height: '10px' }} />
                    <Box
                      sx={{
                        mx: 1,
                        borderRadius: '0 0 6px 6px',
                        p: 2,
                      }}
                    >
                      <MuiNavSection item="ceiling" />
                    </Box>
                  </Collapse>
                )}
                
                {/* Expandable content for furniture */}
                {isFurniture && (
                  <Collapse in={isExpanded} timeout={collapseTimeout} unmountOnExit>
                    <Box sx={{ height: '10px' }} />
                    <Box
                      sx={{
                        mx: 1,
                        borderRadius: '0 0 6px 6px',
                        p: 2,
                      }}
                    >
                      <MuiNavSection item="furnishing" />
                    </Box>
                  </Collapse>
                )}
                
                {/* Expandable content for acoustics */}
                {isAcoustics && (
                  <Collapse in={isExpanded} timeout={collapseTimeout} unmountOnExit>
                    <Box sx={{ height: '20px' }} />
                    <Box
                      sx={{
                        mx: 1,
                        px: 0,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        '& .ms-2': {
                          marginLeft: '0 !important',
                        }
                      }}
                    >
                      <MuiNavSection item="acoustics" />
                    </Box>
                  </Collapse>
                )}
                
                {/* Expandable content for microphones */}
                {isMicrophones && (
                  <Collapse in={isExpanded} timeout={collapseTimeout} unmountOnExit>
                    <Box sx={{ height: '10px' }} />
                    <Box
                      sx={{
                        mx: 1,
                        px: 3,
                        py: 0.5,
                      }}
                    >
                      <MuiNavSection item="microphones" />
                    </Box>
                  </Collapse>
                )}
                
                {/* Expandable content for speakers */}
                {isSpeakers && (
                  <Collapse in={isExpanded} timeout={collapseTimeout} unmountOnExit>
                    <Box sx={{ height: '10px' }} />
                    <Box
                      sx={{
                        mx: 1,
                        px: 3,
                        py: 0.5,
                      }}
                    >
                      <MuiNavSection item="speakers" />
                    </Box>
                  </Collapse>
                )}
                
                {/* Expandable content for camera */}
                {isCamera && (
                  <Collapse in={isExpanded} timeout={collapseTimeout} unmountOnExit>
                    <Box sx={{ height: '10px' }} />
                    <Box
                      sx={{
                        mx: 1,
                        borderRadius: '0 0 6px 6px',
                        p: 2,
                      }}
                    >
                      <MuiNavSection item="camera" />
                    </Box>
                  </Collapse>
                )}
                
                {/* Expandable content for equipment list */}
                {isEquipmentList && (
                  <Collapse in={isExpanded} timeout={collapseTimeout} unmountOnExit>
                    <Box sx={{ height: '10px' }} />
                    <Box
                      sx={{
                        mx: 1,
                        borderRadius: '0 0 6px 6px',
                        p: 2,
                      }}
                    >
                      <MuiNavSection item="equipment_list" />
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>

        {/* Render the active section content for items without custom controls */}
        {active && active !== 'floorplan' && active !== 'ceiling' && active !== 'furnishing' && active !== 'acoustics' && active !== 'microphones' && active !== 'speakers' && active !== 'camera' && active !== 'equipmentlist' && (
          <Box sx={{ 
            mx: 1,
            borderRadius: '6px',
            p: 2,
            mb: 1,
          }}>
            <MuiNavSection />
          </Box>
        )}
      </Box>
      
    </Box>
  );
};

export default MuiNavContainer;
